import type {
  ProtectRequest,
  ProtectionBackend,
  WorkerResponse,
} from "./protection.types";

export type ProtectionProgress = {
  message: string;
  progress: number;
  backend?: ProtectionBackend;
};

type ProtectInput = Omit<ProtectRequest, "type" | "id">;

export function protectImage(
  input: ProtectInput,
  onProgress: (progress: ProtectionProgress) => void,
) {
  const worker = new Worker(new URL("./protection-worker.ts", import.meta.url), {
    type: "module",
  });
  const id = crypto.randomUUID();

  return new Promise<{
    pixels: Uint8ClampedArray;
    backend: ProtectionBackend;
    durationMs: number;
  }>((resolve, reject) => {
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const message = event.data;
      if (message.id !== id) return;

      if (message.type === "status") {
        onProgress({ message: message.message, progress: message.progress });
      } else if (message.type === "ready") {
        onProgress({
          message: message.backend === "webgpu" ? "WebGPU is ready" : "Using the compatibility engine",
          progress: 34,
          backend: message.backend,
        });
      } else if (message.type === "result") {
        worker.terminate();
        resolve(message);
      } else if (message.type === "error") {
        worker.terminate();
        reject(new Error(message.message));
      }
    };

    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || "The local processing worker stopped."));
    };

    const request: ProtectRequest = { type: "protect", id, ...input };
    worker.postMessage(request, [input.modelInput.buffer, input.original.buffer]);
  });
}
